'use client';

import { CustomLink as Link } from '@/components/custom-link';
import { useUser } from '@auth0/nextjs-auth0/client';
import Button from '@/components/inputs/button';
import {
  SIZE,
  VARIANT as BUTTON_VARIANT,
} from '@/components/inputs/button/button.types';
import Carousel from '@/components/data-display/carousel';
import { VARIANT as CAROUSEL_VARIANT } from '@/components/data-display/carousel/carousel.types';
import { VARIANT as ICON_VARIANT } from '@/components/data-display/icon/icon.types';
import registerPartnerClick from '@/utils/register-partner-click/register-partner-click';
import { CLICK_LOCATION } from '@/utils/register-partner-click/register-partner-click.types';
import type { VersatileCardFavourite } from '@/components/integrated/versatile-card/versatile-card.types';
import VersatileCard from '@/components/integrated/versatile-card/versatile-card';
import useHandleFavourites from '@/hooks/use-handle-favourites/use-handle-favourites';
import { useFeatureTogglesContext } from '@/context/feature-toggles/feature-toggles-context';
import { FEATURE_TOGGLE_LIST } from '@/context/feature-toggles/feature-toggles.types';
import { Heading } from '@alto-avios/alto-ui';
import type { RelatedPartnersCarouselProperties } from './related-partners-carousel.types';
import styles from './related-partners-carousel.module.scss';

const RelatedPartnersCarousel = ({
  favouritesLabels,
  relatedPartnersTitle,
  relatedPartners,
  partnerCategory,
  seeAllText,
}: RelatedPartnersCarouselProperties) => {
  const { isFavourite, toggleFavourite } = useHandleFavourites();

  const { user } = useUser();
  const featuresToggles = useFeatureTogglesContext();
  const showFavourite =
    user && !featuresToggles.includes(FEATURE_TOGGLE_LIST.NO_FAVS);

  return (
    <section className={styles['related-partners']}>
      <div className={styles['related-partners__inner']}>
        <div className={styles['related-partners__section-container']}>
          <header className={styles['related-partners__title']}>
            <Heading as="h2" size="sm">
              {relatedPartnersTitle}
            </Heading>
          </header>
        </div>

        <Carousel variant={CAROUSEL_VARIANT.MultiPartners}>
          {relatedPartners.map(relatedPartner => (
            <Carousel.Item
              key={relatedPartner.content.partner?.slug}
              className={styles['related-partners__item']}
            >
              <Link
                href={`/retailers/${relatedPartner.content.partner?.slug}`}
                className={styles['related-partners__link']}
                onClick={() =>
                  registerPartnerClick(
                    CLICK_LOCATION.TOP_PICKS,
                    relatedPartner.content.partner?.slug,
                  )
                }
                title={relatedPartner.content.partner?.name}
                aria-label={relatedPartner.content.partner?.name}
              >
                <VersatileCard
                  {...relatedPartner}
                  favourite={
                    {
                      favouritesLabels,
                      showFavourite,
                      favouriteId: isFavourite(
                        relatedPartner.content.partner?.mechanicId as number,
                      ),
                      toggleFavourite,
                    } as unknown as VersatileCardFavourite
                  }
                  contentClassName={styles['related-partners__card']}
                />
              </Link>
            </Carousel.Item>
          ))}
        </Carousel>
      </div>
      {partnerCategory && (
        <div className={styles['related-partners__section-container']}>
          <div className={styles['related-partners__category-links']}>
            <Button
              className={styles['related-partners__category-link']}
              as="link"
              href={`/${partnerCategory.url}`}
              variant={BUTTON_VARIANT.Text}
              size={SIZE.Medium}
              trailingIcon={ICON_VARIANT.ChevronRight}
              title={partnerCategory.name}
            >
              {partnerCategory.name.replace(/&amp;/g, '&')}
            </Button>
            <Button
              className={styles['related-partners__category-link']}
              as="link"
              href="/retailers"
              variant={BUTTON_VARIANT.Text}
              size={SIZE.Medium}
              trailingIcon={ICON_VARIANT.ChevronRight}
              title={seeAllText}
            >
              {seeAllText}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

export default RelatedPartnersCarousel;
